import { BarChart3, TrendingUp, DollarSign, Activity, AlertCircle, Gauge, Target } from "lucide-react";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { StatCard } from "@/components/dashboard/StatCard";
import { EquityCurve } from "@/components/dashboard/EquityCurve";
import { VolumeChart } from "@/components/dashboard/VolumeChart";
import { LeverageChart } from "@/components/dashboard/LeverageChart";
import { RecentFillsTable } from "@/components/dashboard/RecentFillsTable";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useSubscription } from "@/hooks/useSubscription";
import { useLiveSnapshot } from "@/hooks/useLiveSnapshot";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { MarketType } from "@/lib/hyperliquid";

interface DashboardViewProps {
  address: string;
  defaultRangeDays?: number;
  marketType?: MarketType;
}

export function DashboardView({ address, defaultRangeDays = 30, marketType = 'overview' }: DashboardViewProps) {
  const { subscription, isPro, isLoading: isSubLoading, refresh: refreshSubscription } = useSubscription(address);
  const { data: liveSnapshot } = useLiveSnapshot(address, true);
  const {
    data,
    isLoading,
    isSyncing,
    error,
    lastSynced,
    sync,
    exportCsv,
    rangeDays,
    setRangeDays,
  } = useDashboardData({ address, rangeDays: defaultRangeDays, marketType });

  const headerTitle = marketType === 'perps' ? 'Perps' : marketType === 'spot' ? 'Spot' : 'Dashboard';

  const liveAccountValue = marketType === 'overview'
    ? ((liveSnapshot?.perps.accountValue ?? 0) + (liveSnapshot?.spot.equity ?? 0))
    : marketType === 'spot'
      ? (liveSnapshot?.spot.equity ?? null)
      : (liveSnapshot?.perps.accountValue ?? null);

  const displayAccountValue = marketType === 'overview'
    ? (liveSnapshot && liveSnapshot.perps.accountValue != null && liveSnapshot.spot.equity != null
      ? liveAccountValue
      : (data?.kpis.accountValue ?? null))
    : marketType === 'spot'
      ? (liveSnapshot?.spot.equity != null
        ? liveAccountValue
        : (data?.kpis.accountValue ?? null))
      : (liveSnapshot?.perps.accountValue != null
        ? liveAccountValue
        : (data?.kpis.accountValue ?? null));

  const liveUnrealizedPnl = marketType === 'overview'
    ? (liveSnapshot?.perps.unrealizedPnl ?? null)
    : marketType === 'perps'
      ? (liveSnapshot?.perps.unrealizedPnl ?? null)
      : null;

  const formatValue = (value: number | null | undefined, prefix = "", suffix = "") => {
    if (value == null) return "N/A";
    if (prefix === "$") {
      if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
      return `$${value.toFixed(2)}`;
    }
    return `${prefix}${value.toLocaleString()}${suffix}`;
  };

  const formatSharpe = (value: number | null | undefined) => {
    if (value == null) return null;
    return value.toFixed(2);
  };

  const formatDrawdown = (value: number | null | undefined) => {
    if (value == null) return null;
    return `${(Math.abs(value) * 100).toFixed(1)}%`;
  };

  const formatSignedUsd = (value: number | null | undefined) => {
    if (value == null) return "N/A";
    // Show explicit sign, since pnl can be negative.
    const sign = value >= 0 ? "+" : "-";
    return `${sign}$${Math.abs(value).toFixed(2)}`;
  };

  const formatLeverage = (value: number | null | undefined) => {
    if (value == null) return null;
    return `${value.toFixed(1)}x`;
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value == null) return null;
    return `${(value * 100).toFixed(2)}%`;
  };

  return (
    <main className="container px-3 sm:px-6 py-8 max-w-[1600px]">
      <DashboardHeader
        address={address}
        lastSynced={lastSynced}
        onRefresh={sync}
        onExport={exportCsv}
        isRefreshing={isSyncing}
        isPro={isPro}
        subscription={subscription}
        title={headerTitle}
        rangeDays={rangeDays}
        onRangeDaysChange={setRangeDays}
        onRefreshSubscription={refreshSubscription}
        isRefreshingSubscription={isSubLoading}
      />

      {error && (
        <Alert variant="destructive" className="mb-6 border-destructive/50 bg-destructive/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Primary KPIs Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 mb-6">
        <StatCard
          title="Account Value"
          value={formatValue(displayAccountValue, "$")}
          icon={<DollarSign className="w-4 h-4 text-primary" />}
          loading={isLoading}
          unavailable={displayAccountValue == null}
          tooltip={marketType === 'overview'
            ? "Current combined account value across perps + spot for this wallet (latest synced)."
            : marketType === 'spot'
              ? "Current spot account value for this wallet (latest synced)."
              : "Current perps account value for this wallet (latest synced)."}
        />
        <StatCard
          title="PnL"
          value={formatSignedUsd(data?.kpis.pnl)}
          icon={<TrendingUp className={data?.kpis.pnl != null && data.kpis.pnl < 0 ? "w-4 h-4 text-destructive" : "w-4 h-4 text-primary"} />}
          loading={isLoading}
          unavailable={data?.kpis.pnl == null}
          tooltip="Profit/Loss over the selected date range (end account value minus start account value)."
        />
        <StatCard
          title="Total Fills"
          value={formatValue(data?.kpis.fillsCount)}
          icon={<Activity className="w-4 h-4 text-primary" />}
          loading={isLoading}
          tooltip="Number of trade fills executed in the selected date range."
        />
        <StatCard
          title="Total Volume"
          value={formatValue(data?.kpis.totalVolume, "$")}
          icon={<DollarSign className="w-4 h-4 text-primary" />}
          loading={isLoading}
          tooltip="Total notional traded (sum of fill notional) in the selected date range."
        />
        <StatCard
          title="Sharpe Ratio"
          value={formatSharpe(data?.kpis.sharpe) ?? "N/A"}
          icon={<BarChart3 className="w-4 h-4 text-primary" />}
          loading={isLoading}
          unavailable={data?.kpis.sharpe == null}
          tooltip="Risk-adjusted return metric: average periodic return divided by return volatility over the selected range."
        />
        <StatCard
          title="Max Drawdown"
          value={formatDrawdown(data?.kpis.maxDrawdown) ?? "N/A"}
          icon={<TrendingUp className="w-4 h-4 text-destructive" />}
          loading={isLoading}
          unavailable={data?.kpis.maxDrawdown == null}
          tooltip="Largest peak-to-trough decline in account value over the selected range."
        />
      </div>

      {/* Leverage Metrics Row (for perps) */}
      {(marketType === 'perps' || marketType === 'overview') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          <StatCard
            title="Avg Leverage"
            value={formatLeverage(data?.leverage?.avgLeverage) ?? "N/A"}
            icon={<Gauge className="w-4 h-4 text-primary" />}
            loading={isLoading}
            unavailable={data?.leverage?.avgLeverage == null}
            tooltip="Average effective leverage over the selected range, estimated from trade notional relative to account value."
          />
          <StatCard
            title="Max Leverage"
            value={formatLeverage(data?.leverage?.maxLeverage) ?? "N/A"}
            icon={<Gauge className="w-4 h-4 text-amber-500" />}
            loading={isLoading}
            unavailable={data?.leverage?.maxLeverage == null}
            tooltip="Maximum estimated effective leverage observed over the selected range."
          />
          <StatCard
            title="Lev-Adj Sharpe"
            value={formatSharpe(data?.leverage?.leverageAdjustedSharpe) ?? "N/A"}
            icon={<Target className="w-4 h-4 text-primary" />}
            loading={isLoading}
            unavailable={data?.leverage?.leverageAdjustedSharpe == null}
            tooltip="Sharpe ratio adjusted for leverage - measures skill return per unit of risk"
          />
          <StatCard
            title="Skill Return"
            value={formatPercent(data?.leverage?.skillReturn) ?? "N/A"}
            icon={<Target className="w-4 h-4 text-emerald-500" />}
            loading={isLoading}
            unavailable={data?.leverage?.skillReturn == null}
            tooltip="Return normalized to 1x leverage - what you'd make without leverage amplification"
          />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <EquityCurve
          className="lg:col-span-2"
          data={data?.charts.accountValue}
          loading={isLoading}
          rangeDays={rangeDays}
          currentValue={displayAccountValue}
        />
        <VolumeChart data={data?.charts.volumeDaily} loading={isLoading} />
      </div>

      {/* Leverage Chart (for perps) */}
      {(marketType === 'perps' || marketType === 'overview') && data?.charts?.leverageDaily && data.charts.leverageDaily.length > 0 && (
        <div className="mb-6">
          <LeverageChart data={data.charts.leverageDaily} loading={isLoading} />
        </div>
      )}

      <RecentFillsTable fills={data?.recentFills} loading={isLoading} />
    </main>
  );
}
